import { handleAxiosError } from '@/api/helpers';
import {
  contentSeriesTagColors,
  Course,
  CourseContentType,
  ResourceType,
} from '@/client/courses';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { CustomizeChoiceDialog } from '@/components/courses/CustomizeChoiceDialog';
import { CourseContentPreview } from '@/components/courses/preview/CourseContentPreview';
import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { useCourse } from '@/hooks/query/courses.hooks';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import {
  canPreviewContent,
  determinePreselectedCourseLanguage,
  formatTags,
  SERIES_DURATION,
  stripHtml,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)<{ $maximized: boolean }>`
  &.p-dialog {
    width: ${({ $maximized }) => ($maximized ? '1000px' : '750px')} !important;
    max-width: 95vw !important;
    ${({ $maximized }) =>
      $maximized &&
      `
      height: 880px !important;
      max-height: 90vh !important;
    `}
    border-radius: var(--medium-border-radius);
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    transition: width 0.3s ease, height 0.3s ease;

    .p-dialog-header {
      display: none;
    }

    .p-dialog-content {
      padding: var(--default-padding);
      ${({ $maximized }) =>
        $maximized &&
        `
        display: flex !important;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden !important;
      `}
    }
  }
`;

const StyledPreviewArea = styled.div<{ $maximized: boolean }>`
  position: relative;
  ${({ $maximized }) =>
    $maximized
      ? `
    flex: 1;
    min-height: 0;
    display: flex;
    overflow: hidden;
  `
      : `
    border-radius: var(--default-border-radius);
    overflow: hidden;
    aspect-ratio: 16 / 9;
  `}
`;

const StyledContentPreview = styled(CourseContentPreview)`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  overflow: hidden;
`;

const StyledNotPlayable = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--beige-main);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StyledNotPlayableMessage = styled.p`
  position: absolute;
  max-width: 80%;
  margin: 0;
  padding: var(--small-padding) var(--default-padding);
  text-align: center;
  font-size: var(--small-font-size);
  color: var(--black-main);
  background: rgba(255, 255, 255, 0.85);
  border-radius: var(--default-border-radius);
`;

const StyledBottomSection = styled.div<{ $maximized: boolean }>`
  ${({ $maximized }) =>
    $maximized
      ? `
      flex-shrink: 0;
      position: relative;
      margin-top: var(--default-padding);
      `
      : 'padding-top: var(--default-padding);'}
`;

const StyledCollapsibleInfo = styled.div<{
  $visible: boolean;
  $maximized: boolean;
}>`
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? '200px' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: max-height 0.3s ease, opacity 0.3s ease;
  ${({ $maximized }) =>
    $maximized &&
    `
    position: absolute;
    bottom: 100%;
    left: calc(-1 * var(--default-padding));
    right: calc(-1 * var(--default-padding));
    padding-top: var(--default-padding);
    padding-left: var(--default-padding);
    padding-right: var(--default-padding);
    z-index: 1005;
    background: var(--white-main);
    border-top: 1px solid var(--gray-dark);
    border-radius: var(--default-border-radius);
  `}
`;

const StyledChevronToggle = styled.button<{ $expanded: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--xxsmall-padding);
  color: var(--gray-darker);
  transition: transform 0.3s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  display: flex;
  align-items: center;

  i {
    font-size: var(--small-font-size);
  }

  &:hover {
    color: var(--black-main);
  }
`;

const StyledMetadataRow = styled(FlexContainer)`
  width: 100%;
  margin-bottom: var(--small-padding);

  .metadata-item {
    display: flex;
    align-items: center;
    gap: var(--xxsmall-padding);
    font-size: var(--xsmall-font-size);
    color: var(--gray-darker);

    i {
      font-size: var(--xsmall-font-size);
    }
  }
`;

const StyledTitle = styled.h2`
  font-size: var(--heading-small-font-size);
  font-weight: 700;
  color: var(--black-main);
  margin: 0 0 var(--xsmall-padding) 0;
  line-height: 1.3;
`;

const StyledDescription = styled.p`
  font-size: var(--small-font-size);
  color: var(--gray-darkest);
  line-height: 1.5;
  margin: 0 0 var(--default-padding) 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
`;

type CoursePreviewOverlayProps = {
  courseId: string | null;
  onHide: () => void;
  onCustomizeManually: (course: Course) => void;
};

export const CoursePreviewOverlay: React.FC<CoursePreviewOverlayProps> = ({
  courseId,
  onHide,
  onCustomizeManually,
}) => {
  const { t } = useTranslation();
  const { isRestrictedByRole } = usePermission();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const [showCustomizeChoice, setShowCustomizeChoice] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [maxStartIndex, setMaxStartIndex] = useState(0);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const initializedRef = useRef<string | null>(null);

  useEffect(() => {
    if (courseId) {
      // Refetch the course and its content (resources, slide decks, quizzes)
      // on open so freshly-uploaded/edited media shows immediately instead of
      // stale cached data.
      queryClient.invalidateQueries(['course', courseId]);
      queryClient.invalidateQueries(['course-resource', courseId]);
      queryClient.invalidateQueries(['course-slide-deck', courseId]);
      queryClient.invalidateQueries(['course-quiz', courseId]);
    } else {
      initializedRef.current = null;
      setIsMaximized(false);
      setMaxStartIndex(0);
      setShowCustomizeChoice(false);
    }
  }, [courseId, queryClient]);

  const { course, isLoading, isFetched } = useCourse({
    courseId: courseId ?? undefined,
    enabled: !!courseId,
    cacheTime: 60_000,
    onError: (e) => handleAxiosError(e as Error | AxiosError, toast),
  });

  useEffect(() => {
    if (isFetched && !course) {
      onHide();
    }
  }, [isFetched, course, onHide]);

  const sortedContents = useMemo(
    () =>
      course?.courseContents
        ? [...course.courseContents].sort((a, b) => a.position - b.position)
        : [],
    [course],
  );

  const firstModuleIsVideo =
    sortedContents[0]?.type === CourseContentType.RESOURCE &&
    sortedContents[0]?.resource?.type === ResourceType.VIDEO;

  // Non-previewable content (e.g. a future release) still opens the overlay,
  // but its content cannot be played back.
  const isPlayable = canPreviewContent(course, currentUser);

  // Decide the initial preview mode once the course data has loaded
  useEffect(() => {
    if (!course?.id || initializedRef.current === course.id) return;
    initializedRef.current = course.id;

    const hasContent = !!sortedContents.length;

    if (!isPlayable || !hasContent || firstModuleIsVideo) {
      setIsMaximized(false);
    } else {
      setIsMaximized(true);
      setMaxStartIndex(0);
    }
  }, [course?.id, firstModuleIsVideo]);

  const isCourseAiCustomized =
    !!course?.customizationMetadata?.originalContentId;
  const isCourseCloned = !!course?.cloneMetadata?.isClone;
  const canAiCustomize =
    !account?.isSystem &&
    !!course?.aiCustomizationSettings?.isEnabled &&
    !isCourseAiCustomized &&
    !isCourseCloned;

  const canClone = !!course?.cloneSettings?.isCloneable;

  const showCustomizeButton = canAiCustomize || canClone;

  const isCustomizeRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.COURSES,
  );

  const duration = course?.contentSeries
    ? SERIES_DURATION[course.contentSeries]
    : null;

  const displayLanguage = determinePreselectedCourseLanguage(
    currentUser,
    course ?? null,
  );
  const matchingTranslation = course?.courseTranslations?.find(
    (ct) => ct.language === displayLanguage,
  );
  const displayName = matchingTranslation?.name || course?.name || '';
  const rawDescription =
    matchingTranslation?.description || course?.description || '';
  const displayDescription = stripHtml(rawDescription);

  const handleModuleAdvance = useCallback(() => {
    if (!isMaximized) setIsMaximized(true);
  }, [isMaximized]);

  const handleCustomizeClick = useCallback(() => {
    setShowCustomizeChoice(true);
  }, []);

  const handleAiCustomize = useCallback(() => {
    if (course) {
      navigate(RedirectPaths[RedirectPathsEnum.COURSES_CUSTOMIZE](course.id));
      setShowCustomizeChoice(false);
      onHide();
    }
  }, [course, navigate, onHide]);

  const handleManualCustomize = useCallback(() => {
    if (course) {
      setShowCustomizeChoice(false);
      onHide();
      onCustomizeManually(course);
    }
  }, [course, onHide, onCustomizeManually]);

  const handleViewDetails = useCallback(() => {
    if (!course) return;
    navigate(RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](course.id));
    onHide();
  }, [course, navigate, onHide]);

  const tags = course?.tags;
  const hasTags = !!tags?.length;
  const hasMetadata = !!course?.contentSeries || hasTags;

  const dialogProps = {
    $maximized: isMaximized,
    visible: !!courseId,
    onHide,
    blockScroll: true,
    draggable: false,
    modal: true,
    dismissableMask: true,
    pt: { root: { dir: document.documentElement.dir || 'ltr' } },
  };

  const customizeChoiceDialog = (
    <CustomizeChoiceDialog
      visible={showCustomizeChoice}
      onHide={() => setShowCustomizeChoice(false)}
      onAiCustomize={handleAiCustomize}
      onManualCustomize={handleManualCustomize}
      canAiCustomize={canAiCustomize}
      canClone={canClone}
    />
  );

  if (isLoading) {
    return (
      <>
        <StyledDialog {...dialogProps}>
          <FlexContainer
            direction="column"
            align="center"
            style={{ padding: 'var(--large-padding) 0' }}
          >
            <ProgressSpinner />
          </FlexContainer>
        </StyledDialog>
        {customizeChoiceDialog}
      </>
    );
  }

  // Should not be called, dialog should be hidden from useEffect
  if (!course) {
    return <StyledDialog {...dialogProps} />;
  }

  return (
    <>
      <StyledDialog {...dialogProps}>
        <StyledPreviewArea $maximized={isMaximized}>
          {isPlayable ? (
            <StyledContentPreview
              course={course}
              initialModuleIndex={maxStartIndex}
              isPreviewModal
              isMaximized={isMaximized}
              language={displayLanguage}
              onModuleAdvance={handleModuleAdvance}
              onExitPreview={onHide}
            />
          ) : (
            <StyledNotPlayable>
              {course.courseThumbnail?.signedUrl && (
                <img src={course.courseThumbnail.signedUrl} alt={displayName} />
              )}
              <StyledNotPlayableMessage>
                {t('content.preview.notPlayable')}
              </StyledNotPlayableMessage>
            </StyledNotPlayable>
          )}
        </StyledPreviewArea>

        <StyledBottomSection $maximized={isMaximized}>
          <StyledCollapsibleInfo
            $visible={!isMaximized || infoExpanded}
            $maximized={isMaximized}
          >
            {hasMetadata && (
              <StyledMetadataRow justify="space-between" align="center">
                {course.contentSeries && (
                  <AppChip
                    label={t(`content.series.${course.contentSeries}`)}
                    type={contentSeriesTagColors[course.contentSeries]}
                    size="s"
                  />
                )}
                <FlexContainer
                  gap="var(--small-padding)"
                  align="center"
                  justify="flex-end"
                >
                  {hasTags && (
                    <span className="metadata-item">
                      <i className="pi pi-tag" />
                      {formatTags(tags)}
                    </span>
                  )}
                  {duration && (
                    <span className="metadata-item">
                      <i className="pi pi-clock" />
                      {duration} {t('generic.minutes')}
                    </span>
                  )}
                </FlexContainer>
              </StyledMetadataRow>
            )}

            {displayName && <StyledTitle>{displayName}</StyledTitle>}

            {displayDescription && (
              <StyledDescription>{displayDescription}</StyledDescription>
            )}
          </StyledCollapsibleInfo>

          <FlexContainer
            gap="var(--small-padding)"
            justify="flex-start"
            align="center"
            style={
              isMaximized
                ? {
                    background: 'var(--white-main)',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                  }
                : undefined
            }
          >
            <FlexContainer gap="var(--small-padding)" justify="flex-start">
              {showCustomizeButton && (
                <ViewOnlyRestrictionWhen restricted={isCustomizeRestricted}>
                  <AppButton
                    label={t('content.preview.customize')}
                    isDisabled={isCustomizeRestricted}
                    onClick={
                      isCustomizeRestricted ? undefined : handleCustomizeClick
                    }
                  />
                </ViewOnlyRestrictionWhen>
              )}
              <AppButton
                label={t('content.view.details')}
                type="outlined"
                onClick={handleViewDetails}
              />
            </FlexContainer>
            {isMaximized && (
              <StyledChevronToggle
                $expanded={infoExpanded}
                onClick={() => setInfoExpanded(!infoExpanded)}
              >
                <i className="pi pi-chevron-up" />
              </StyledChevronToggle>
            )}
          </FlexContainer>
        </StyledBottomSection>
      </StyledDialog>

      {customizeChoiceDialog}
    </>
  );
};
